import React from 'react';

import { Control, Controller } from 'react-hook-form';

import { Checkbox, FormControlLabel, FormGroup, Grid, MenuItem, Stack, Switch, TextField } from '@mui/material';

import {
  TOUR_OFFER_TRANSFER_TYPE_LABELS,
  TourOfferTransferInput,
  TourOfferTransferType,
} from '~/types/tours/tourOffer';

import { TOUR_TRAVELLER_REQUIREMENT_DOCUMENT, TOUR_TRAVELLER_REQUIREMENT_DOCUMENT_LABELS } from '../constants';

import NativeTourCard from './NativeTourCard';
import NativeTourLocationSearch from './NativeTourLocationSearch';
import { NativeTourForm } from './nativeTourForm';

interface Props {
  control: Control<NativeTourForm>;
}

const ID_LEVELS = { passport: 'Passport', photo_id: 'Photo ID' } as const;
const NOT_APPLICABLE = 'notApplicable';
const TRANSFER_TYPES = Object.keys(TOUR_OFFER_TRANSFER_TYPE_LABELS) as Array<TourOfferTransferType>;
const EMPTY_TRANSFER: TourOfferTransferInput = {
  arrivalAirport: null,
  arrivalTransferType: null,
  departureAirport: null,
  departureTransferType: null,
};

/** What travellers must give before they go, and the tour's airport transfers. */
export default function NativeTourTravellersSection({ control }: Props) {
  return (
    <Stack spacing={3}>
      <NativeTourCard title="Traveller requirements" description="What every traveller must give before departure.">
        <Controller
          control={control}
          name="travellers.requirements"
          render={({ field }) => {
            const set = (change: Partial<typeof field.value>) => field.onChange({ ...field.value, ...change });
            return (
              <Stack spacing={2}>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <TextField
                      select
                      label="ID level required"
                      value={field.value.idLevelRequired}
                      onChange={(event) =>
                        set({ idLevelRequired: event.target.value as typeof field.value.idLevelRequired })
                      }
                      fullWidth
                    >
                      {(Object.keys(ID_LEVELS) as Array<keyof typeof ID_LEVELS>).map((level) => (
                        <MenuItem key={level} value={level}>
                          {ID_LEVELS[level]}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid size={{ xs: 12, md: 8 }}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={field.value.weightRequired}
                            onChange={(event) => set({ weightRequired: event.target.checked })}
                          />
                        }
                        label="Weight required"
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            checked={field.value.flightDetailsRequired}
                            onChange={(event) => set({ flightDetailsRequired: event.target.checked })}
                          />
                        }
                        label="Flight details required"
                      />
                    </Stack>
                  </Grid>
                </Grid>
                <FormGroup>
                  {TOUR_TRAVELLER_REQUIREMENT_DOCUMENT.map((document) => (
                    <FormControlLabel
                      key={document}
                      control={
                        <Checkbox
                          checked={field.value.documentsRequired.includes(document)}
                          onChange={(event) =>
                            set({
                              documentsRequired: event.target.checked
                                ? [...field.value.documentsRequired, document]
                                : field.value.documentsRequired.filter((other) => other !== document),
                            })
                          }
                        />
                      }
                      label={TOUR_TRAVELLER_REQUIREMENT_DOCUMENT_LABELS[document]}
                    />
                  ))}
                </FormGroup>
                <NativeTourLocationSearch
                  label="Departure airports"
                  types={['airport']}
                  value={field.value.departureAirports}
                  onChange={(departureAirports) => set({ departureAirports })}
                  helperText="The airports travellers can fly out from"
                />
              </Stack>
            );
          }}
        />
      </NativeTourCard>

      <NativeTourCard
        title="Airport transfers"
        description="The tour's own arrival and departure transfer. An option's itinerary can set its own."
      >
        <Controller
          control={control}
          name="travellers.transfer"
          render={({ field }) => {
            const transfer = field.value ?? EMPTY_TRANSFER;
            const set = (change: Partial<TourOfferTransferInput>) => {
              const next = { ...transfer, ...change };
              field.onChange(Object.values(next).some((value) => value !== null) ? next : null);
            };
            const typeField = (label: string, name: 'arrivalTransferType' | 'departureTransferType') => (
              <TextField
                select
                label={label}
                value={transfer[name] ?? NOT_APPLICABLE}
                onChange={(event) =>
                  set({
                    [name]:
                      event.target.value === NOT_APPLICABLE ? null : (event.target.value as TourOfferTransferType),
                  })
                }
                fullWidth
              >
                <MenuItem value={NOT_APPLICABLE}>Not applicable</MenuItem>
                {TRANSFER_TYPES.map((type) => (
                  <MenuItem key={type} value={type}>
                    {TOUR_OFFER_TRANSFER_TYPE_LABELS[type]}
                  </MenuItem>
                ))}
              </TextField>
            );
            return (
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Stack spacing={2}>
                    <NativeTourLocationSearch
                      label="Arrival airport"
                      multiple={false}
                      types={['airport']}
                      value={transfer.arrivalAirport ? [transfer.arrivalAirport] : []}
                      onChange={([airport]) => set({ arrivalAirport: airport ?? null })}
                    />
                    {typeField('Arrival transfer', 'arrivalTransferType')}
                  </Stack>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Stack spacing={2}>
                    <NativeTourLocationSearch
                      label="Departure airport"
                      multiple={false}
                      types={['airport']}
                      value={transfer.departureAirport ? [transfer.departureAirport] : []}
                      onChange={([airport]) => set({ departureAirport: airport ?? null })}
                    />
                    {typeField('Departure transfer', 'departureTransferType')}
                  </Stack>
                </Grid>
              </Grid>
            );
          }}
        />
      </NativeTourCard>
    </Stack>
  );
}
